import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, CheckCircle, AlertCircle, ShieldCheck } from 'lucide-react'
import { authService } from '@/services/auth'
import { useAuthStore } from '@/stores/authStore'
import { getErrorMessage } from '@/services/api'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'

/**
 * CompleteProfile — shown after first login.
 * Forces customer to set a new password and confirm their mobile number.
 * Clears is_first_login on success.
 */
export default function CompleteProfile() {
  const navigate = useNavigate()
  const { login: storeLogin, user } = useAuthStore()

  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [mobile, setMobile] = useState(user?.mobile_number || '')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [showMobileConfirm, setShowMobileConfirm] = useState(false)

  const passwordChecks = [
    { label: 'At least 8 characters', ok: password.length >= 8 },
    { label: 'One uppercase letter', ok: /[A-Z]/.test(password) },
    { label: 'One lowercase letter', ok: /[a-z]/.test(password) },
    { label: 'One number', ok: /[0-9]/.test(password) },
    { label: 'One special character', ok: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password) },
  ]
  const passwordValid = passwordChecks.every((c) => c.ok)

  // Validate, then make the customer EXPLICITLY confirm their mobile number
  // before saving — login codes go to this number and a wrong one locks them out.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== passwordConfirm) {
      setError('Passwords do not match.')
      return
    }
    if (!passwordValid) {
      setError('Please meet all password requirements.')
      return
    }
    if (!mobile.trim()) {
      setError('Please enter your mobile number.')
      return
    }

    setShowMobileConfirm(true)
  }

  const submitProfile = async () => {
    setShowMobileConfirm(false)
    setIsLoading(true)
    try {
      const res = await authService.completeProfile({
        new_password: password,
        new_password_confirm: passwordConfirm,
        new_mobile: mobile.trim(),
      })
      // Update the auth store with refreshed user data
      const accessToken = localStorage.getItem('access_token') || ''
      const refreshToken = localStorage.getItem('refresh_token') || ''
      storeLogin(accessToken, refreshToken, res.user)
      setDone(true)
      setTimeout(() => navigate('/account'), 2000)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">You're all set!</h2>
          <p className="text-gray-600">Your account is ready. Taking you to your dashboard...</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-gray-50">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="mx-auto w-14 h-14 bg-primary-50 rounded-full flex items-center justify-center mb-3">
            <ShieldCheck className="text-primary-600" size={28} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Complete Your Account</h1>
          <p className="mt-2 text-sm text-gray-600">
            Welcome{user?.first_name ? `, ${user.first_name}` : ''}! Please set a secure password and
            confirm your mobile number before continuing.
          </p>
        </div>

        <Card className="p-6 sm:p-8">
          {error && <Alert variant="error" message={error} onClose={() => setError(null)} className="mb-4" />}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* New password */}
            <div className="relative">
              <Input
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {password && (
              <ul className="space-y-1 text-sm">
                {passwordChecks.map((c) => (
                  <li key={c.label} className={`flex items-center gap-2 ${c.ok ? 'text-green-600' : 'text-gray-400'}`}>
                    {c.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                    {c.label}
                  </li>
                ))}
              </ul>
            )}

            {/* Confirm password */}
            <div className="relative">
              <Input
                label="Confirm Password"
                type={showConfirm ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {passwordConfirm && password !== passwordConfirm && (
              <p className="text-sm text-red-600">Passwords do not match.</p>
            )}

            {/* Mobile number */}
            <Input
              label="Your Mobile Number"
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="e.g. 07700 900000"
              autoComplete="tel"
              required
            />
            <div className="-mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
              <p className="text-xs text-amber-800">
                <strong>Important — check this carefully.</strong> Every time you sign in we text your
                login code to this number. If it's wrong, you won't be able to log back in. Change it
                here now if needed — you can also update it later under <strong>My Account → Profile</strong>.
              </p>
            </div>

            <Button
              type="submit"
              fullWidth
              isLoading={isLoading}
              disabled={!passwordValid || password !== passwordConfirm || !mobile.trim()}
            >
              Save &amp; Continue to My Account
            </Button>
          </form>
        </Card>
      </div>

      {/* Mobile confirmation popup — must explicitly confirm before the account is saved */}
      {showMobileConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={24} />
              <div>
                <h3 className="text-lg font-bold text-gray-900">Is this your correct mobile number?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  We text your login code to this number <strong>every time you sign in</strong>. If it's
                  wrong, you won't be able to log back in.
                </p>
              </div>
            </div>

            <div className="my-4 rounded-lg bg-gray-50 border border-gray-200 py-3 text-center">
              <p className="text-2xl font-bold tracking-wide text-gray-900">{mobile.trim() || '—'}</p>
            </div>

            <div className="flex flex-col gap-2">
              <Button fullWidth onClick={submitProfile} isLoading={isLoading}>
                Yes, this number is correct
              </Button>
              <Button fullWidth variant="outline" onClick={() => setShowMobileConfirm(false)}>
                No, let me change it
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
